import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { UserPlus, Edit, Mail, Phone } from 'lucide-react';
import { ContactDialog } from './ContactDialog';
import { users } from '../../../data/sampleData';

interface Contact {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  avatar?: string;
  type: 'internal' | 'external';
}

const contacts: Contact[] = [
  {
    id: '1',
    name: 'John Smith',
    role: 'Lead Attorney',
    email: 'john.smith@lawfirm.com',
    phone: '(555) 123-4567',
    avatar: users[0].avatar,
    type: 'internal',
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    role: 'Paralegal',
    email: 'sarah.johnson@lawfirm.com',
    phone: '(555) 234-5678',
    avatar: users[1].avatar,
    type: 'internal',
  },
  {
    id: '4',
    name: 'Robert Johnson',
    role: 'Client',
    email: 'robert.johnson@example.com',
    phone: '(555) 345-6789',
    avatar: users[3].avatar,
    type: 'external',
  },
  {
    id: '5',
    name: 'Dr. Emily Williams',
    role: 'Expert Witness',
    email: 'emily.williams@medical.com',
    phone: '(555) 456-7890',
    type: 'external',
  },
];

export const ContactsTab: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setDialogOpen(true);
  };

  const handleSave = (contactData: Partial<Contact>) => {
    console.log('Saving contact:', contactData);
    setDialogOpen(false);
    setSelectedContact(null);
  };

  const ContactCard = ({ contact }: { contact: Contact }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              src={contact.avatar}
              alt={contact.name}
              sx={{ width: 48, height: 48 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {contact.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contact.role}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Chip
              label={contact.type === 'internal' ? 'Staff' : 'External'}
              size="small"
              color={contact.type === 'internal' ? 'primary' : 'default'}
              variant="outlined"
              sx={{ mb: 1 }}
            />
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Mail size={16} className="text-gray-400" />
                <Typography variant="body2" noWrap>
                  {contact.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone size={16} className="text-gray-400" />
                <Typography variant="body2">
                  {contact.phone}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            borderTop: 1,
            borderColor: 'divider',
            pt: 2,
            mt: 1,
          }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<Mail size={16} />}
              href={`mailto:${contact.email}`}
            >
              Email
            </Button>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<Phone size={16} />}
              href={`tel:${contact.phone}`}
            >
              Call
            </Button>
            <IconButton
              size="small"
              onClick={() => handleEdit(contact)}
              sx={{ ml: 'auto' }}
            >
              <Edit size={18} />
            </IconButton>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Paper>
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Case Contacts</Typography>
        <Button
          variant="contained"
          startIcon={<UserPlus size={18} />}
          onClick={() => setDialogOpen(true)}
          fullWidth={isMobile}
        >
          Add Contact
        </Button>
      </Box>

      <Box sx={{ p: 2 }}>
        {isMobile ? (
          <Stack spacing={2}>
            {contacts.map((contact) => (
              <ContactCard key={contact.id} contact={contact} />
            ))}
          </Stack>
        ) : (
          <List>
            {contacts.map((contact, index) => (
              <React.Fragment key={contact.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        href={`mailto:${contact.email}`}
                        size="small"
                        color="primary"
                      >
                        <Mail size={18} />
                      </IconButton>
                      <IconButton 
                        href={`tel:${contact.phone}`}
                        size="small"
                        color="primary"
                      >
                        <Phone size={18} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEdit(contact)}
                        size="small"
                      >
                        <Edit size={18} />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={contact.avatar}>
                      {!contact.avatar && contact.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {contact.name}
                        <Chip
                          label={contact.type === 'internal' ? 'Staff' : 'External'}
                          size="small"
                          color={contact.type === 'internal' ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {contact.role}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {contact.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {contact.phone}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < contacts.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      <ContactDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedContact(null);
        }}
        onSave={handleSave}
        contact={selectedContact}
      />
    </Paper>
  );
};